import React, {useState, useEffect, useRef} from "react"
import {Box} from "@mui/material"
import {useShoppingBagGetCallbackObservable, useModalsCloseObservable} from "@next/eventservice"
import MyAccountLoggedInTooltip, {MyAccountLoggedInTooltipProps} from "../MyAccountLoggedInTooltip"
import MyAccountUsernameLinkContainer from "../MyAccountUsernameLinkContainer"
import {PublishToModalsClosed} from "../../events/modalsClosed"
import connect from "./connect"
import {WideViewIconContainer, Username, NarrowViewIconContainer, Wrapper} from "./component"
import {HEADER_NAV_BAR_QUICK_LINKS, HEADER_NAV_BAR_QUICK_LINKS_MY_ACCOUNT, NO_FOLLOW} from "../../config/constants"
import {removeDeptFromSessionStorage} from "../../utils/removeDeptFromSessionStorage"

const MyAccountLink = ({src, accessibilityText, url, dataTestId, showUserName = true, myAccountText = ""}) => {
    const showUserNameComponent = myAccountText && showUserName
    return (
        <MyAccountUsernameLinkContainer
            href={url as string}
            data-ga-v1={HEADER_NAV_BAR_QUICK_LINKS}
            data-ga-v2={HEADER_NAV_BAR_QUICK_LINKS_MY_ACCOUNT}
            rel={NO_FOLLOW}
            data-testid={dataTestId as string}
        >
            <img src={src as string} alt={accessibilityText as string} />
            {showUserNameComponent && (
                <Username data-testid="header-myaccount-username" data-dtrum-replay-mask="" variant="body2">
                    {myAccountText}
                </Username>
            )}
        </MyAccountUsernameLinkContainer>
    )
}

export type MyAccountProps = {
    accessibilityText: string | null
    isLoggedIn: boolean
    myAccountText: string | null
    narrowModeIcon: string
    wideModeIcon: string
    tooltipIcon: string
    url: string
    signoutUrl: string
    updateShoppingBag: (data: any) => void
    userUpdated: boolean
    text: any
    firstName: string
    showUserName: boolean | undefined
    // eslint-disable-next-line react/require-default-props
    tooltipPlacement?: Pick<MyAccountLoggedInTooltipProps, "placement">["placement"]
    // eslint-disable-next-line react/require-default-props
    tooltipEnableModArrow?: Pick<MyAccountLoggedInTooltipProps, "enableModArrow">["enableModArrow"]
    showCheckoutButton: boolean
}

export const MyAccount = ({
    showUserName = true,
    accessibilityText,
    myAccountText,
    isLoggedIn,
    url,
    signoutUrl,
    narrowModeIcon,
    tooltipIcon,
    tooltipPlacement,
    tooltipEnableModArrow,
    wideModeIcon,
    updateShoppingBag,
    userUpdated,
    text,
    firstName,
    showCheckoutButton,
}: MyAccountProps) => {
    useShoppingBagGetCallbackObservable(data => {
        return updateShoppingBag(data)
    })

    const [open, setOpen] = useState(false)
    const anchorEl = useRef(null)

    const handleClose = event => {
        if (event) event.preventDefault()
        if (event?.type === "scroll" && event?.target !== document) return
        setOpen(false)
    }

    const handleClick = event => {
        if (!open) PublishToModalsClosed()
        event.preventDefault()
        setOpen(!open)
    }

    useEffect(() => {
        if (open) window.addEventListener("scroll", handleClose, true)
        else window.removeEventListener("scroll", handleClose)
    }, [open])

    useModalsCloseObservable(() => handleClose(null))

    const onClick = isLoggedIn ? {onClick: handleClick} : {onClick: removeDeptFromSessionStorage}
    return (
        <Wrapper
            className="header-adaptive-my-account"
            ref={anchorEl}
            data-testid="header-adaptive-my-account"
            usernameVisible={(myAccountText && !showCheckoutButton && showUserName) as boolean}
        >
            <Box display={{xs: "block", md: "none"}}>
                <NarrowViewIconContainer
                    onClick={removeDeptFromSessionStorage}
                    data-testid="header-adaptive-my-account-smallIcon"
                >
                    <MyAccountLink
                        src={narrowModeIcon}
                        accessibilityText={accessibilityText}
                        url={url}
                        showUserName
                        dataTestId="header-adaptive-my-account-smallIcon-container-link"
                    />
                </NarrowViewIconContainer>
            </Box>

            <Box display={{xs: "none", md: "block"}}>
                <WideViewIconContainer
                    {...onClick}
                    showContainer={showUserName || userUpdated}
                    data-testid="header-adaptive-my-account-icon-container"
                >
                    <MyAccountLink
                        src={wideModeIcon}
                        accessibilityText={accessibilityText}
                        url={url}
                        showUserName={showUserName}
                        myAccountText={myAccountText || ""}
                        dataTestId="header-adaptive-my-account-icon-container-link"
                    />
                </WideViewIconContainer>
                {isLoggedIn && (
                    <MyAccountLoggedInTooltip
                        open={open}
                        anchorEl={anchorEl}
                        handleClose={handleClose}
                        tooltipIcon={tooltipIcon}
                        accessibilityText={accessibilityText || ""}
                        url={url}
                        text={text}
                        firstName={firstName}
                        signoutUrl={signoutUrl}
                        placement={tooltipPlacement}
                        enableModArrow={tooltipEnableModArrow}
                    />
                )}
            </Box>
        </Wrapper>
    )
}

export default connect(MyAccount) as any
